'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  Layers,
  CheckCircle2,
  Star,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  FileText,
  Users,
  Zap,
  Shield,
  Award,
  Target,
  Info,
  PlusCircle,
  FolderOpen,
  ScrollText,
  GitMerge,
} from 'lucide-react';

const STEPS = [
  { id: 'wall-gap', label: 'The Wall & Gaps', icon: Layers },
  { id: 'stories-archives', label: 'Stories & Archives', icon: FolderOpen },
  { id: 'certainty', label: 'Certainty Labels', icon: CheckCircle2 },
  { id: 'reputation', label: 'Reputation System', icon: Star },
  { id: 'best-practices', label: 'Best Practices', icon: BookOpen },
];

// ── Step 1: Wall Gap ──────────────────────────────────────────────────────────
function WallGapStep() {
  const [hoveredBlock, setHoveredBlock] = useState<string | null>(null);

  const demoBlocks = [
    { id: 'b1', label: 'Great Zimbabwe', published: 18, min: 12, status: 'filled' },
    { id: 'b2', label: 'Gold Trade Routes', published: 11, min: 10, status: 'filled' },
    { id: 'b3', label: 'Khoisan Interaction', published: 0, min: 6, status: 'gap' },
    { id: 'b4', label: 'Oral Law Traditions', published: 4, min: 10, status: 'partial' },
    { id: 'b5', label: 'Chimurenga Wars', published: 22, min: 12, status: 'filled' },
    { id: 'b6', label: 'Diaspora Communities', published: 2, min: 8, status: 'gap' },
    { id: 'b7', label: 'Women in Liberation', published: 3, min: 8, status: 'partial' },
    { id: 'b8', label: 'BSAC Concessions', published: 2, min: 6, status: 'gap' },
  ];

  const statusConfig = {
    filled: { label: 'Covered', color: '#E05A47', bg: 'var(--wall-filled)', border: '#E05A4755', cssClass: 'wall-block-filled' },
    partial: { label: 'Partially covered', color: '#F39C12', bg: '#F0C8A0', border: '#F39C1266', cssClass: 'wall-block-partial' },
    gap: { label: 'Gap — needs contributions', color: '#5A5A5A', bg: 'transparent', border: 'var(--wall-gap)', cssClass: 'wall-block-gap' },
  };

  const hovered = hoveredBlock ? demoBlocks.find((b) => b.id === hoveredBlock) : null;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          What is The Wall?
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          The Wall is Griott's living map of historical knowledge. Each block represents a curated topic within a historical era. The goal is to fill every block with enough verified contributions to reach its minimum coverage threshold.
        </p>
      </div>

      {/* Interactive wall demo */}
      <div
        className="rounded-xl border p-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <p className="text-xs font-medium mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Hover over a block to inspect it
        </p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {demoBlocks.map((block) => {
            const cfg = statusConfig[block.status as keyof typeof statusConfig];
            const pct = Math.min(100, Math.round((block.published / block.min) * 100));
            return (
              <button
                key={block.id}
                className={`relative rounded-lg p-3 text-left transition-all duration-200 ${cfg.cssClass}`}
                style={{ minHeight: 72 }}
                onMouseEnter={() => setHoveredBlock(block.id)}
                onMouseLeave={() => setHoveredBlock(null)}
                onFocus={() => setHoveredBlock(block.id)}
                onBlur={() => setHoveredBlock(null)}
              >
                <span
                  className="block text-xs font-medium leading-tight mb-1"
                  style={{ color: block.status === 'gap' ? 'var(--muted-foreground)' : 'var(--foreground)' }}
                >
                  {block.label}
                </span>
                <div className="h-1 rounded-full mt-auto" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: cfg.color }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        {/* Tooltip */}
        <div
          className="rounded-lg border p-3 transition-all duration-200 min-h-[64px] flex items-center gap-3"
          style={{
            backgroundColor: 'var(--muted)',
            borderColor: hovered ? statusConfig[hovered.status as keyof typeof statusConfig].color + '55' : 'var(--border)',
          }}
        >
          {hovered ? (
            <>
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: statusConfig[hovered.status as keyof typeof statusConfig].color }}
              />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{hovered.label}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {hovered.published} of {hovered.min} contributions · {statusConfig[hovered.status as keyof typeof statusConfig].label}
                </p>
              </div>
            </>
          ) : (
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Hover a block above to see its coverage details
            </p>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { cssClass: 'wall-block-filled', label: 'Covered', desc: 'Reached minimum contributions' },
          { cssClass: 'wall-block-partial', label: 'Partial', desc: 'Some contributions, needs more' },
          { cssClass: 'wall-block-gap', label: 'Gap', desc: 'No contributions yet — priority target' },
        ].map((item) => (
          <div
            key={item.label}
            className={`rounded-lg p-3 ${item.cssClass}`}
          >
            <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--foreground)' }}>{item.label}</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.desc}</p>
          </div>
        ))}
      </div>

      <div
        className="rounded-lg border-l-4 p-4 flex gap-3"
        style={{ backgroundColor: '#E05A4710', borderLeftColor: 'var(--primary)' }}
      >
        <Target size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          <span className="font-semibold" style={{ color: 'var(--foreground)' }}>Your mission:</span> Find a gap or partial block that matches your knowledge and submit a contribution to help fill it. Gap topics earn bonus reputation points.
        </p>
      </div>
    </div>
  );
}

// ── Step 2: Stories vs Archives ───────────────────────────────────────────────
function StoriesArchivesStep() {
  const [activeTab, setActiveTab] = useState<'story' | 'archive'>('story');

  const storyFeatures = [
    { icon: ScrollText, label: 'A single historical account', desc: 'One event, person, moment, or documented fact.' },
    { icon: CheckCircle2, label: 'Carries a certainty label', desc: 'Oral Tradition, Sourced Claim, or Firsthand Account.' },
    { icon: Users, label: 'Reviewed by a curator', desc: 'Goes through the moderation pipeline before publication.' },
    { icon: TrendingUp, label: 'Earns reputation points', desc: 'Published stories build your contributor score.' },
  ];

  const archiveFeatures = [
    { icon: FolderOpen, label: 'A curated collection of stories', desc: 'Groups multiple stories around a theme, era, or region.' },
    { icon: GitMerge, label: 'Created and owned by a contributor', desc: 'The archive creator controls which stories are accepted in.' },
    { icon: Layers, label: 'Second layer of curation', desc: 'Archive creators review story requests on top of the main pipeline.' },
    { icon: Sparkles, label: 'AI-powered archive matching', desc: 'When submitting, AI suggests which archive your story belongs to.' },
  ];

  const flowSteps = [
    { label: 'You write a Story', color: '#E05A47', icon: ScrollText },
    { label: 'Curator reviews & publishes', color: '#008080', icon: CheckCircle2 },
    { label: 'AI suggests an Archive', color: '#7B6FA3', icon: Sparkles },
    { label: 'Archive creator accepts', color: '#F39C12', icon: FolderOpen },
    { label: 'Story joins the collection', color: '#2C5E3B', icon: GitMerge },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          Stories vs Archives
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Griott has two types of content. Understanding the difference helps you contribute more effectively and get your work into the right collections.
        </p>
      </div>

      {/* Tab switcher */}
      <div
        className="rounded-xl border p-1 flex gap-1"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {(['story', 'archive'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
            style={{
              backgroundColor: activeTab === tab ? 'var(--primary)' : 'transparent',
              color: activeTab === tab ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
            }}
          >
            {tab === 'story' ? <ScrollText size={14} /> : <FolderOpen size={14} />}
            {tab === 'story' ? 'Story' : 'Archive'}
          </button>
        ))}
      </div>

      {/* Detail panel */}
      {activeTab === 'story' ? (
        <div className="space-y-4">
          <div
            className="rounded-xl border p-5"
            style={{ backgroundColor: 'var(--card)', borderColor: '#E05A4744' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <ScrollText size={18} style={{ color: '#E05A47' }} />
              <h3 className="text-base font-bold" style={{ color: '#E05A47' }}>Story</h3>
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: '#E05A4718', color: '#E05A47' }}
              >
                What you submit
              </span>
            </div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
              A Story is a single, self-contained historical account. It documents one event, person, practice, or piece of knowledge tied to a specific era and topic. Every contribution you submit is a Story.
            </p>
            <div className="space-y-3">
              {storyFeatures.map((f) => (
                <div key={f.label} className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#E05A4718' }}
                  >
                    <f.icon size={13} style={{ color: '#E05A47' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{f.label}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className="rounded-lg border-l-4 p-4 flex gap-3"
            style={{ backgroundColor: '#E05A4710', borderLeftColor: '#E05A47' }}
          >
            <Info size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#E05A47' }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              <span className="font-semibold" style={{ color: 'var(--foreground)' }}>Example:</span> "The Role of Spirit Mediums in the First Chimurenga" is a Story — a focused account of a specific historical phenomenon.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div
            className="rounded-xl border p-5"
            style={{ backgroundColor: 'var(--card)', borderColor: '#7B6FA344' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen size={18} style={{ color: '#7B6FA3' }} />
              <h3 className="text-base font-bold" style={{ color: '#7B6FA3' }}>Archive</h3>
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: '#7B6FA318', color: '#7B6FA3' }}
              >
                A collection of stories
              </span>
            </div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
              An Archive is a curated collection that groups multiple published Stories around a broader subject — a dynasty, a movement, a cultural practice, or a region. Archive creators act as a second layer of editorial curation.
            </p>
            <div className="space-y-3">
              {archiveFeatures.map((f) => (
                <div key={f.label} className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#7B6FA318' }}
                  >
                    <f.icon size={13} style={{ color: '#7B6FA3' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{f.label}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className="rounded-lg border-l-4 p-4 flex gap-3"
            style={{ backgroundColor: '#7B6FA310', borderLeftColor: '#7B6FA3' }}
          >
            <Info size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#7B6FA3' }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              <span className="font-semibold" style={{ color: 'var(--foreground)' }}>Example:</span> "The Chimurenga Wars Collection" is an Archive — it pulls together dozens of individual Stories about resistance, spirit mediums, and colonial conflict into one navigable body of work.
            </p>
          </div>
        </div>
      )}

      {/* How they connect */}
      <div>
        <p className="text-xs font-semibold mb-3" style={{ color: 'var(--muted-foreground)' }}>
          HOW STORIES BECOME PART OF AN ARCHIVE
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          {flowSteps.map((step, idx) => (
            <React.Fragment key={step.label}>
              <div className="flex items-center gap-2 sm:flex-col sm:items-center sm:text-center flex-1 min-w-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: step.color + '22' }}
                >
                  <step.icon size={14} style={{ color: step.color }} />
                </div>
                <p className="text-xs leading-tight" style={{ color: 'var(--muted-foreground)' }}>
                  {step.label}
                </p>
              </div>
              {idx < flowSteps.length - 1 && (
                <ChevronRight
                  size={14}
                  className="flex-shrink-0 hidden sm:block"
                  style={{ color: 'var(--muted-foreground)' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Archive submission tip */}
      <div
        className="rounded-xl border p-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={15} style={{ color: 'var(--primary)' }} />
          <h4 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
            AI Archive Matching on the Submission Form
          </h4>
        </div>
        <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--muted-foreground)' }}>
          When you submit a Story, the submission form includes an <strong style={{ color: 'var(--foreground)' }}>Add to an Archive</strong> section. Click <strong style={{ color: 'var(--foreground)' }}>"Suggest Archive with AI"</strong> and our AI will read your story and recommend the best existing archive — or suggest starting a new one if none fits.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div
            className="rounded-lg p-3"
            style={{ backgroundColor: '#2C5E3B11', borderLeft: '3px solid #2C5E3B' }}
          >
            <p className="text-xs font-semibold mb-1" style={{ color: '#2C5E3B' }}>Add to existing archive</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Your story is submitted to the archive creator for review. If accepted, it joins the collection.
            </p>
          </div>
          <div
            className="rounded-lg p-3"
            style={{ backgroundColor: '#7B6FA311', borderLeft: '3px solid #7B6FA3' }}
          >
            <p className="text-xs font-semibold mb-1" style={{ color: '#7B6FA3' }}>Start a new archive</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              If no archive fits, you can propose a new one. Once your story is published, the archive is created with you as its curator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Certainty Labels ──────────────────────────────────────────────────
function CertaintyStep() {
  const [selected, setSelected] = useState<string | null>(null);

  const labels = [
    {
      id: 'oral',
      name: 'Oral Tradition',
      cssClass: 'certainty-oral',
      color: '#7B6FA3',
      icon: Users,
      description: 'Knowledge passed down through generations via storytelling, song, or ceremony. Not corroborated by written records.',
      examples: ['Clan origin stories', 'Praise poetry (izibongo)', 'Ceremonial accounts'],
      when: 'Use when your source is an elder, community narrative, or oral account without written backup.',
      caution: 'Always note the community, region, and approximate generation of the source.',
    },
    {
      id: 'sourced',
      name: 'Sourced',
      cssClass: 'certainty-sourced',
      color: '#008080',
      icon: FileText,
      description: 'Backed by written records, academic publications, archival documents, or credible secondary sources.',
      examples: ['Colonial records', 'Academic journals', 'Newspaper archives', 'Published books'],
      when: 'Use when you can cite a specific document, publication, or archival record.',
      caution: 'Include full citation: author, title, year, page number where possible.',
    },
    {
      id: 'firsthand',
      name: 'Firsthand',
      cssClass: 'certainty-firsthand',
      color: '#94D4A4',
      icon: Zap,
      description: 'Direct personal witness or participant account. The contributor was present or directly interviewed a primary witness.',
      examples: ['Personal memoir', 'Recorded interview', 'Eyewitness account'],
      when: 'Use only when you personally witnessed the event or conducted a direct interview.',
      caution: 'Firsthand accounts carry high weight — curators will verify the claim carefully.',
    },
  ];

  const sel = selected ? labels.find((l) => l.id === selected) : null;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          Certainty Labeling System
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Every contribution must carry a certainty label. This tells readers and curators how confident you are in your source — and helps the archive maintain scholarly integrity without excluding oral or community knowledge.
        </p>
      </div>

      {/* Label selector */}
      <div className="grid grid-cols-3 gap-3">
        {labels.map((label) => (
          <button
            key={label.id}
            onClick={() => setSelected(selected === label.id ? null : label.id)}
            className={`rounded-xl border p-4 text-left transition-all duration-200 ${label.cssClass}`}
            style={{
              outline: selected === label.id ? `2px solid ${label.color}` : 'none',
              outlineOffset: 2,
              transform: selected === label.id ? 'scale(1.02)' : 'scale(1)',
            }}
          >
            <label.icon size={18} className="mb-2" style={{ color: label.color }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{label.name}</p>
            <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>{label.description}</p>
          </button>
        ))}
      </div>

      {/* Detail panel */}
      <div
        className="rounded-xl border overflow-hidden transition-all duration-300"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: sel ? sel.color + '44' : 'var(--border)',
          minHeight: 160,
        }}
      >
        {sel ? (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <sel.icon size={16} style={{ color: sel.color }} />
              <h3 className="text-sm font-bold" style={{ color: sel.color }}>{sel.name}</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>When to use</p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{sel.when}</p>
              </div>
              <div>
                <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Examples</p>
                <ul className="space-y-1">
                  {sel.examples.map((ex) => (
                    <li key={ex} className="text-xs flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: sel.color }} />
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div
              className="rounded-lg p-3 flex gap-2"
              style={{ backgroundColor: sel.color + '11', borderLeft: `3px solid ${sel.color}` }}
            >
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" style={{ color: sel.color }} />
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{sel.caution}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-40">
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Select a label above to learn when and how to use it
            </p>
          </div>
        )}
      </div>

      <div
        className="rounded-lg border-l-4 p-4 flex gap-3"
        style={{ backgroundColor: '#7B6FA311', borderLeftColor: '#7B6FA3' }}
      >
        <Info size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#7B6FA3' }} />
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          <span className="font-semibold" style={{ color: 'var(--foreground)' }}>Honest labeling matters.</span> Mislabeling a sourced claim as firsthand — or vice versa — is grounds for dispute and reputation penalties. When in doubt, choose the more conservative label.
        </p>
      </div>
    </div>
  );
}

// ── Step 3: Reputation ────────────────────────────────────────────────────────
function ReputationStep() {
  const [simulatedScore, setSimulatedScore] = useState(50);
  const [published, setPublished] = useState(0);
  const [corroborations, setCorroborations] = useState(0);
  const [disputed, setDisputed] = useState(0);

  useEffect(() => {
    const score = 50 + published * 10 + corroborations - disputed * 15;
    setSimulatedScore(Math.max(0, score));
  }, [published, corroborations, disputed]);

  const tiers = [
    { label: 'New Contributor', min: 0, max: 49, color: '#5A5A5A', cssClass: 'tier-new' },
    { label: 'Trusted Contributor', min: 50, max: 199, color: '#008080', cssClass: 'tier-trusted' },
    { label: 'Historian', min: 200, max: 599, color: '#E05A47', cssClass: 'tier-historian' },
    { label: 'Elder Contributor', min: 600, max: Infinity, color: '#F39C12', cssClass: 'tier-elder' },
  ];

  const currentTier = tiers.find((t) => simulatedScore >= t.min && simulatedScore <= t.max) || tiers[0];
  const nextTier = tiers.find((t) => t.min > simulatedScore);
  const progressPct = nextTier
    ? Math.round(((simulatedScore - currentTier.min) / (nextTier.min - currentTier.min)) * 100)
    : 100;

  const actions = [
    { label: 'Published contributions', icon: TrendingUp, color: '#2C5E3B', value: `+${published * 10}`, formula: '× 10 pts each', state: published, setter: setPublished, max: 20 },
    { label: 'Corroborations received', icon: TrendingUp, color: '#008080', value: `+${corroborations}`, formula: '× 1 pt each', state: corroborations, setter: setCorroborations, max: 50 },
    { label: 'Disputed entries', icon: TrendingDown, color: '#B03020', value: `-${disputed * 15}`, formula: '× −15 pts each', state: disputed, setter: setDisputed, max: 10 },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          Reputation Mechanics
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Your reputation score reflects the quality and reliability of your contributions. It unlocks higher tiers, curator privileges, and community trust. Use the simulator below to understand how actions affect your score.
        </p>
      </div>

      {/* Simulator */}
      <div
        className="rounded-xl border p-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <p className="text-xs font-semibold mb-4" style={{ color: 'var(--muted-foreground)' }}>
          REPUTATION SIMULATOR — drag sliders to explore
        </p>

        <div className="space-y-4 mb-6">
          {actions.map((action) => (
            <div key={action.label}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  <action.icon size={13} style={{ color: action.color }} />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{action.label}</span>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}>({action.formula})</span>
                </div>
                <span className="text-xs font-mono font-semibold" style={{ color: action.color, fontFamily: 'var(--font-mono)' }}>
                  {action.value}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={action.max}
                  value={action.state}
                  onChange={(e) => action.setter(Number(e.target.value))}
                  className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: action.color }}
                />
                <span
                  className="text-xs font-mono w-6 text-right"
                  style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)' }}
                >
                  {action.state}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Score display */}
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div
              className="text-4xl font-bold tabular-nums"
              style={{ color: currentTier.color, fontFamily: 'var(--font-mono)', transition: 'color 0.3s' }}
            >
              {simulatedScore}
            </div>
            <div className="text-xs mt-0.5" style={{ color: currentTier.color }}>{currentTier.label}</div>
          </div>
          <div className="flex-1">
            {nextTier && (
              <>
                <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>
                  <span>{currentTier.label}</span>
                  <span>{nextTier.label} at {nextTier.min}</span>
                </div>
                <div className="h-2 rounded-full" style={{ backgroundColor: 'var(--muted)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%`, backgroundColor: currentTier.color }}
                  />
                </div>
                <p className="text-xs mt-1 text-right" style={{ color: 'var(--muted-foreground)' }}>
                  {nextTier.min - simulatedScore} pts to next tier
                </p>
              </>
            )}
            {!nextTier && (
              <div className="flex items-center gap-2">
                <Award size={16} style={{ color: '#F39C12' }} />
                <span className="text-sm font-semibold" style={{ color: '#F39C12' }}>Maximum tier reached!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiers.map((tier) => (
          <div
            key={tier.label}
            className={`rounded-lg p-3 ${tier.cssClass}`}
            style={{
              outline: currentTier.label === tier.label ? `2px solid ${tier.color}` : 'none',
              outlineOffset: 2,
            }}
          >
            <p className="text-xs font-semibold" style={{ color: tier.color }}>{tier.label}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}>
              {tier.max === Infinity ? `${tier.min}+ pts` : `${tier.min}–${tier.max} pts`}
            </p>
          </div>
        ))}
      </div>

      <div
        className="rounded-lg border-l-4 p-4 flex gap-3"
        style={{ backgroundColor: '#E07B3A11', borderLeftColor: '#E07B3A' }}
      >
        <Shield size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#E07B3A' }} />
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          <span className="font-semibold" style={{ color: 'var(--foreground)' }}>Disputes cost 15 pts.</span> A single disputed entry can wipe out the gains from a published contribution. Quality over quantity is always the right strategy.
        </p>
      </div>
    </div>
  );
}

// ── Step 4: Best Practices ────────────────────────────────────────────────────
function BestPracticesStep() {
  const [openItem, setOpenItem] = useState<number | null>(0);

  const practices = [
    {
      icon: Target,
      color: '#E05A47',
      title: 'Target gaps first',
      summary: 'Gap topics earn bonus visibility and community recognition.',
      detail: 'Before submitting, visit The Wall and filter by "Gaps Only". Topics with zero contributions are the highest priority for the archive. Your contribution to a gap topic is more impactful than adding a 20th entry to an already-covered topic. Curators also fast-track gap submissions.',
    },
    {
      icon: FileText,
      color: '#008080',
      title: 'Cite everything you can',
      summary: 'Even oral sources should be attributed as specifically as possible.',
      detail: 'For written sources: include author, title, year, publisher, and page number. For oral sources: note the name (or role) of the source, their community, approximate age, and when you heard the account. For firsthand accounts: describe your relationship to the event and when it occurred. Vague citations are the #1 reason for rejection.',
    },
    {
      icon: Users,
      color: '#7B6FA3',
      title: 'Respect oral knowledge',
      summary: 'Oral tradition is not lesser — it is differently evidenced.',
      detail: 'Griott was built to preserve knowledge that written archives have excluded. Oral tradition contributions are fully valid and valued. The certainty label system exists to distinguish evidence types, not to rank them. An oral account labeled correctly is far more valuable than a sourced claim mislabeled as firsthand.',
    },
    {
      icon: Sparkles,
      color: '#2C5E3B',
      title: 'Write for a general audience',
      summary: 'Assume your reader knows the era but not the specific topic.',
      detail: 'Avoid jargon without explanation. Define acronyms on first use. Provide brief context for figures, places, and events that may be unfamiliar. Your contribution will be read by students, diaspora community members, researchers, and curious members of the public — write for all of them.',
    },
    {
      icon: AlertTriangle,
      color: '#E07875',
      title: 'Avoid these common mistakes',
      summary: 'Mislabeling, vague citations, and unsupported claims are the top rejection reasons.',
      detail: 'The most common rejection reasons are: (1) certainty label does not match the source type, (2) no citation or attribution provided, (3) content duplicates an existing published contribution without adding new information, (4) submission covers a topic outside the listed historical eras, (5) content is opinion or analysis rather than documented historical account.',
    },
    {
      icon: TrendingUp,
      color: '#F39C12',
      title: 'Build your reputation steadily',
      summary: 'Consistent quality beats volume every time.',
      detail: 'New contributors should aim for 2–3 well-researched submissions before scaling up. Each published contribution earns 10 reputation points; each dispute costs 15. A single disputed entry undoes the gains of a published one. Take time to review the curator feedback on your first submissions before submitting more.',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          Best Practices for Submissions
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          These guidelines are distilled from curator feedback and community experience. Following them gives your submission the best chance of being published quickly.
        </p>
      </div>

      <div className="space-y-2">
        {practices.map((item, idx) => (
          <div
            key={item.title}
            className="rounded-xl border overflow-hidden transition-all duration-200"
            style={{
              backgroundColor: 'var(--card)',
              borderColor: openItem === idx ? item.color + '55' : 'var(--border)',
            }}
          >
            <button
              className="w-full flex items-center gap-3 p-4 text-left"
              onClick={() => setOpenItem(openItem === idx ? null : idx)}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: item.color + '18' }}
              >
                <item.icon size={15} style={{ color: item.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{item.title}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{item.summary}</p>
              </div>
              <ChevronRight
                size={15}
                className="flex-shrink-0 transition-transform duration-200"
                style={{
                  color: 'var(--muted-foreground)',
                  transform: openItem === idx ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
              />
            </button>
            {openItem === idx && (
              <div
                className="px-4 pb-4 pt-0"
                style={{ borderTop: `1px solid ${item.color}22` }}
              >
                <p className="text-sm leading-relaxed pt-3" style={{ color: 'var(--muted-foreground)' }}>
                  {item.detail}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div
        className="rounded-xl border p-6 text-center"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <Award size={32} className="mx-auto mb-3" style={{ color: 'var(--primary)' }} />
        <h3 className="text-base font-bold mb-1" style={{ color: 'var(--foreground)' }}>
          You're ready to contribute
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Head to The Wall, find a gap that matches your knowledge, and make your first submission.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            href="/the-wall"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            <Layers size={15} />
            Explore The Wall
          </Link>
          <Link
            href="/contribution-submission"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border transition-all duration-150 active:scale-95"
            style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--muted)' }}
          >
            <PlusCircle size={15} />
            Submit a Contribution
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ContributorGuidePage() {
  const [currentStep, setCurrentStep] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const goTo = (idx: number) => {
    setCurrentStep(idx);
    contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stepComponents = [WallGapStep, StoriesArchivesStep, CertaintyStep, ReputationStep, BestPracticesStep];
  const StepComponent = stepComponents[currentStep];

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <span
            className="text-xs font-mono px-2 py-0.5 rounded"
            style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}
          >
            New Contributor Guide
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          How Griott Works
        </h1>
        <p className="text-sm max-w-xl" style={{ color: 'var(--muted-foreground)' }}>
          A short interactive tour covering the five things every contributor needs to understand before their first submission.
        </p>
      </div>

      {/* Step nav */}
      <div
        className="rounded-xl border p-1 mb-8 flex gap-1 overflow-x-auto"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {STEPS.map((step, idx) => {
          const isActive = idx === currentStep;
          const isDone = idx < currentStep;
          return (
            <button
              key={step.id}
              onClick={() => goTo(idx)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 flex-shrink-0 flex-1 justify-center sm:justify-start"
              style={{
                backgroundColor: isActive ? 'var(--primary)' : 'transparent',
                color: isActive ? 'var(--primary-foreground)' : isDone ? 'var(--foreground)' : 'var(--muted-foreground)',
              }}
            >
              {isDone ? (
                <CheckCircle2 size={14} style={{ color: isActive ? 'var(--primary-foreground)' : '#2C5E3B' }} />
              ) : (
                <step.icon size={14} />
              )}
              <span className="hidden sm:inline">{step.label}</span>
              <span className="sm:hidden">{idx + 1}</span>
            </button>
          );
        })}
      </div>

      {/* Step progress indicator */}
      <div className="flex items-center gap-2 mb-6">
        <div className="flex gap-1.5">
          {STEPS.map((_, idx) => (
            <div
              key={`dot-${idx}`}
              className="rounded-full transition-all duration-300"
              style={{
                width: idx === currentStep ? 20 : 6,
                height: 6,
                backgroundColor: idx <= currentStep ? 'var(--primary)' : 'var(--muted)',
              }}
            />
          ))}
        </div>
        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Step {currentStep + 1} of {STEPS.length}
        </span>
      </div>

      {/* Step content */}
      <div ref={contentRef}>
        <div
          className="rounded-xl border p-6 sm:p-8 mb-6"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <StepComponent />
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => goTo(currentStep - 1)}
          disabled={currentStep === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--muted)' }}
        >
          <ChevronLeft size={15} />
          Previous
        </button>

        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          {STEPS[currentStep].label}
        </span>

        {currentStep < STEPS.length - 1 ? (
          <button
            onClick={() => goTo(currentStep + 1)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            Next
            <ChevronRight size={15} />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <Link
              href="/the-wall"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-150"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--muted)' }}
            >
              Go to The Wall
              <ArrowRight size={15} />
            </Link>
            <Link
              href="/doc-projects"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              <FolderOpen size={15} />
              Start a Project
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
